import { Box, Grid, Skeleton, Typography } from "@mui/material";
import {
  AgeIcon,
  WeightIcon,
  HeightIcon,
  StrongFootIcon,
  PreferredFormationIcon,
  CountriesCoachedInIcon,
  TeamsCoachedIcon,
  RepresentedClubIcon,
  TotalCareerTransfersIcon,
  TotalScoutedPlayersIcon,
  LocationsIcon,
  TotalRepresentedIcon,
  TryoutIcon,
  JobsIcon,
} from "../../../assets/icons/MUI/EntitiesIcons";
import EntityOverviewCard from "../../../shared/Entity/EntityOverviewCard";
import { colors } from "../../../theme/palette";
import { Card, Country } from "../../../types";
import EntityMap from "./EntityMap";
// @ts-ignore
import Flag from "react-world-flags";
import { useTranslation } from "react-i18next";

// Mapping card keys to icon components
const iconsMapping: { [key: string]: React.ElementType } = {
  age: AgeIcon,
  weight: WeightIcon,
  height: HeightIcon,
  strongFoot: StrongFootIcon,
  preferredFormations: PreferredFormationIcon,
  countriesCoachedIn: CountriesCoachedInIcon,
  totalTeamsCoached: TeamsCoachedIcon,
  representedClub: RepresentedClubIcon,
  totalCareerTransfers: TotalCareerTransfersIcon,
  totalScoutedPlayers: TotalScoutedPlayersIcon,
  locations: LocationsIcon,
  totalRepresented: TotalRepresentedIcon,
  tryout: TryoutIcon,
  jobs: JobsIcon,
};

const EntityOverview = ({
  entityName,
  cards,
  countries,
  isLoading,
}: {
  entityName: string;
  cards: Card[];
  countries: Country[];
  isLoading: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "8px",
        overflow: "hidden",
        padding: 2,
      }}
    >
      <Typography
        variant="body16Bold"
        sx={{
          display: "block",
          marginBottom: "24px",
          fontWeight: 700,
          color: colors.Black100,
        }}
      >
        {t(`pages.entity.${entityName.toLowerCase()}Overview`)}
      </Typography>
      <Box
        sx={{
          paddingBottom: "21px",
          display: "flex",
          gap: "16px",
          alignItems: "stretch",
        }}
      >
        {cards.map((card, index) => {
          const IconComponent =
            iconsMapping[card.key] ||
            (() => (
              <Box
                sx={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  backgroundColor: colors.Green100,
                }}
              />
            ));
          const value = Array.isArray(card.value) ? card.value.join(",") : card.value;
          return <EntityOverviewCard icon={IconComponent} title={card.key} value={value} isLoading={isLoading} />;
        })}
      </Box>
      <Typography
        variant="body16ExtraBold"
        sx={{
          display: "block",
          marginBottom: "20px",
          marginTop: "20px",
        }}
      >
        {t("pages.entity.countries")}
      </Typography>
      <Box
        sx={{
          display: "flex",
          gap: "30px",
          flexDirection: {
            xs: "column",
            sm: "row",
          },
        }}
      >
        {isLoading ? (
          <>
            <Skeleton variant="rounded" width={287} height={215} sx={{ display: "inline-block" }} />

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                rowGap: "12px",
                maxHeight: "270px",
                overflowY: "scroll",
                width: "calc(100% - 317px)",
              }}
            >
              {[...Array(5)].map((_, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    columnGap: "8px",
                  }}
                >
                  <Skeleton variant="rounded" width={20} height={16} />

                  <Typography variant="body2" sx={{ fontWeight: 600, maxWidth: "95%" }}>
                    <Skeleton variant="rounded" width={50} />
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        ) : (
          <>
            <EntityMap countries={countries} />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                rowGap: "12px",
                maxHeight: "270px",
                overflowY: "scroll",
                width: "100%",
              }}
            >
              {countries.map(({ country, countryCode }) => (
                <Box
                  key={country}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Flag
                    code={countryCode}
                    style={{
                      width: "20px",
                      height: "16px",
                      marginRight: "4px",
                    }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600, maxWidth: "95%" }}>
                    {country}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default EntityOverview;
